import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { CATEGORIE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileUp, Loader2, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";

export default function InvoiceImportPage() {
  const nav = useNavigate();
  const [pdfBase64, setPdfBase64] = useState("");
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [data, setData] = useState(null);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("PDF troppo grande (max 8MB)"); return; }
    const r = new FileReader();
    r.onload = () => { setPdfBase64(r.result); setFileName(f.name); };
    r.readAsDataURL(f);
  };

  const parse = async () => {
    if (!pdfBase64) return;
    setParsing(true);
    try {
      const { data } = await api.post("/invoices/parse", { pdf_base64: pdfBase64 });
      setData(data);
      toast.success(`Estratte ${data.lines?.length || 0} righe`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore estrazione"); }
    finally { setParsing(false); }
  };

  const updateLine = (i, k, v) => {
    setData(d => { const lines = [...d.lines]; lines[i] = { ...lines[i], [k]: v }; return { ...d, lines }; });
  };
  const removeLine = (i) => setData(d => ({ ...d, lines: d.lines.filter((_, idx) => idx !== i) }));

  const confirm = async () => {
    setImporting(true);
    try {
      const { data: res } = await api.post("/invoices/import", {
        fornitore: data.fornitore, numero_fattura: data.numero_fattura, data_fattura: data.data_fattura,
        pdf_base64: pdfBase64,
        lines: data.lines.map(l => ({
          codice_fornitore: l.codice_fornitore || "", descrizione: l.descrizione,
          quantita: parseFloat(l.quantita) || 0, prezzo_unitario: parseFloat(l.prezzo_unitario) || 0,
          importo_totale: parseFloat(l.importo_totale) || 0,
          product_id: l.product_id || null,
          categoria: l.categoria || "Altro", marca: l.marca || data.fornitore, posizione: l.posizione || "",
        })),
      });
      toast.success(`Import completato: ${res.movimenti} movimenti, ${res.prodotti_creati} nuovi prodotti`);
      nav("/movimenti");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore import"); }
    finally { setImporting(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">AI</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">Import Fattura PDF</h1>
        <p className="text-zinc-400 text-sm mt-1">Carica un PDF fattura. L'AI estrae automaticamente fornitore, numero e righe articolo per generare i carichi.</p>
      </div>

      {!data && (
        <div className="gm-card p-8">
          <label className="block border-2 border-dashed border-zinc-700 hover:border-[#4CAF50] p-12 text-center cursor-pointer transition-colors" data-testid="invoice-upload-zone">
            <FileUp className="mx-auto text-zinc-500 mb-3" size={36} />
            <div className="text-sm uppercase tracking-widest text-zinc-400">{fileName || "Trascina o clicca per caricare PDF"}</div>
            <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid="invoice-file-input" />
          </label>
          {pdfBase64 && (
            <button onClick={parse} disabled={parsing} className="gm-btn-primary w-full h-14 mt-5 rounded-sm flex items-center justify-center gap-2" data-testid="invoice-parse-button">
              {parsing ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle2 size={20} />}
              {parsing ? "Estrazione AI in corso..." : "Analizza con AI"}
            </button>
          )}
        </div>
      )}

      {data && (
        <>
          <div className="gm-card p-5 mb-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Fornitore"><input value={data.fornitore || ""} onChange={e=>setData(d=>({...d, fornitore: e.target.value}))} className="gm-input" /></Field>
            <Field label="Numero fattura"><input value={data.numero_fattura || ""} onChange={e=>setData(d=>({...d, numero_fattura: e.target.value}))} className="gm-input font-mono-data" /></Field>
            <Field label="Data fattura"><input value={data.data_fattura || ""} onChange={e=>setData(d=>({...d, data_fattura: e.target.value}))} className="gm-input font-mono-data" /></Field>
          </div>

          <div className="gm-card overflow-x-auto mb-5">
            <table className="gm-table">
              <thead>
                <tr><th>Stato</th><th>Cod. Fornitore</th><th>Descrizione</th><th>Cat.</th><th className="text-right">Qtà</th><th className="text-right">€/unità</th><th className="text-right">Totale</th><th></th></tr>
              </thead>
              <tbody>
                {data.lines?.map((l, i) => (
                  <tr key={i} data-testid="invoice-line-row">
                    <td>{l.existing ? <span className="gm-badge gm-badge-success">Esiste</span> : <span className="gm-badge gm-badge-warning">Nuovo</span>}</td>
                    <td><input value={l.codice_fornitore || ""} onChange={e=>updateLine(i,"codice_fornitore",e.target.value)} className="bg-transparent border-b border-zinc-700 focus:border-[#4CAF50] outline-none font-mono-data text-sm w-32 py-1" /></td>
                    <td><input value={l.descrizione || ""} onChange={e=>updateLine(i,"descrizione",e.target.value)} className="bg-transparent border-b border-zinc-700 focus:border-[#4CAF50] outline-none text-sm w-full py-1" /></td>
                    <td>
                      {l.existing ? <span className="text-xs text-zinc-500">—</span> : (
                        <select value={l.categoria || "Altro"} onChange={e=>updateLine(i,"categoria",e.target.value)} className="bg-transparent border-b border-zinc-700 outline-none text-xs py-1">
                          {CATEGORIE.map(c=><option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                    </td>
                    <td className="text-right"><input type="number" step="any" value={l.quantita || 0} onChange={e=>updateLine(i,"quantita",e.target.value)} className="bg-transparent border-b border-zinc-700 focus:border-[#4CAF50] outline-none font-mono-data text-sm w-16 text-right py-1" /></td>
                    <td className="text-right"><input type="number" step="0.01" value={l.prezzo_unitario || 0} onChange={e=>updateLine(i,"prezzo_unitario",e.target.value)} className="bg-transparent border-b border-zinc-700 focus:border-[#4CAF50] outline-none font-mono-data text-sm w-20 text-right py-1" /></td>
                    <td className="text-right font-mono-data text-zinc-400">{l.importo_totale?.toFixed(2)}</td>
                    <td><button onClick={()=>removeLine(i)} className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1.5"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-2">
              <AlertTriangle className="text-[#FF9F0A]" size={14} />
              {data.lines?.filter(l=>!l.existing).length} prodotti nuovi verranno creati automaticamente
            </div>
            <div className="flex gap-2">
              <button onClick={()=>{setData(null); setPdfBase64(""); setFileName("");}} className="h-12 px-5 border border-zinc-700 hover:border-white uppercase text-xs tracking-widest font-bold">Annulla</button>
              <button onClick={confirm} disabled={importing} className="gm-btn-primary h-12 px-6 rounded-sm flex items-center gap-2" data-testid="invoice-confirm-button">
                {importing ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />}
                {importing ? "Import in corso..." : `Conferma carico di ${data.lines?.length} righe`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const Field = ({label, children}) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">{label}</label>
    {children}
  </div>
);
